import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import '../styles/pages/IngredientCheckResultPage.css';

/**
 * IngredientCheckResultPage - 成分チェック結果表示ページ
 * つむぎデザインシステム準拠、BEM記法
 * アコーディオンで詳細を展開可能
 */
const IngredientCheckResultPage = () => {
  const navigate = useNavigate();

  const [result, setResult] = useState(null);
  const [expandedAlerts, setExpandedAlerts] = useState({});

  // ページタイトル設定
  useEffect(() => {
    document.title = '成分チェック結果 | つむぎ';
  }, []);

  useEffect(() => {
    // localStorageから結果を取得
    const resultStr = localStorage.getItem('ingredientCheckResult');

    if (!resultStr) {
      // 結果がない場合はチェックページに戻る
      navigate('/ingredient-check');
      return;
    }

    try {
      const parsedResult = JSON.parse(resultStr);
      setResult(parsedResult);
    } catch (err) {
      console.error('Failed to parse result:', err);
      navigate('/ingredient-check');
    }
  }, [navigate]);

  /**
   * アコーディオン展開/折りたたみ
   */
  const toggleAlert = (category, index) => {
    const key = `${category}-${index}`;
    setExpandedAlerts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /**
   * もう一度チェック
   */
  const handleCheckAgain = () => {
    localStorage.removeItem('ingredientCheckResult');
    navigate('/ingredient-check');
  };

  /**
   * レシピ一覧に戻る
   */
  const handleBackToRecipes = () => {
    localStorage.removeItem('ingredientCheckResult');
    navigate('/recipe-hub');
  };

  if (!result) {
    return null;
  }

  const { ingredients, alerts, usage } = result;
  const hasAlerts = alerts.hashimoto_high.length > 0 || alerts.general_caution.length > 0 || alerts.moderation.length > 0;

  /**
   * アラートカードコンポーネント
   */
  const AlertCard = ({ alert, category, index }) => {
    const key = `${category}-${index}`;
    const isExpanded = expandedAlerts[key];

    return (
      <div className={`ingredient-check-result__alert-card ingredient-check-result__alert-card--${alert.color}`}>
        <button
          className="ingredient-check-result__alert-header"
          onClick={() => toggleAlert(category, index)}
        >
          <div className="ingredient-check-result__alert-header-left">
            <span className="ingredient-check-result__alert-icon">{alert.icon}</span>
            <div>
              <h4 className="ingredient-check-result__alert-title">{alert.title}</h4>
              <p className="ingredient-check-result__alert-ingredient">
                検出: {alert.ingredientName}
              </p>
            </div>
          </div>
          <div className="ingredient-check-result__alert-header-right">
            <span className={`ingredient-check-result__alert-badge ingredient-check-result__alert-badge--${alert.color}`}>
              {alert.badge}
            </span>
            <span className={`ingredient-check-result__alert-toggle ${isExpanded ? 'ingredient-check-result__alert-toggle--expanded' : ''}`}>
              ▼
            </span>
          </div>
        </button>

        {isExpanded && (
          <div className="ingredient-check-result__alert-details">
            <div className="ingredient-check-result__alert-section">
              <h5 className="ingredient-check-result__alert-section-title">なぜ注意が必要？</h5>
              <p className="ingredient-check-result__alert-section-text">{alert.description}</p>
            </div>

            <div className="ingredient-check-result__alert-section">
              <h5 className="ingredient-check-result__alert-section-title">どうすればいい？</h5>
              <p className="ingredient-check-result__alert-section-text">{alert.detail}</p>
            </div>

            <div className="ingredient-check-result__alert-section">
              <h5 className="ingredient-check-result__alert-section-title">科学的根拠</h5>
              <p className="ingredient-check-result__alert-section-text ingredient-check-result__alert-section-text--source">
                {alert.source}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AppLayout>
      <PageTitle
        title="成分チェック結果"
        subtitle="つむぎが分析しました"
        icon="✨"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="px-3 py-2 mt-4">
        {/* 検出された成分一覧 */}
        <div className="tsumugi-card ingredient-check-result__ingredients mb-3">
          <h3 className="tsumugi-card-title">📝 検出された成分（{ingredients.length}個）</h3>
          <div className="ingredient-check-result__ingredients-grid">
            {ingredients.map((ingredient, index) => (
              <span key={index} className="ingredient-check-result__ingredient-tag">
                {ingredient}
              </span>
            ))}
          </div>
        </div>

        {/* 橋本病特有の注意成分 */}
        {alerts.hashimoto_high.length > 0 && (
          <div className="ingredient-check-result__section mb-4">
            <h3 className="ingredient-check-result__section-title ingredient-check-result__section-title--hashimoto">
              ⚠️ 橋本病特有の注意成分
            </h3>
            <p className="ingredient-check-result__section-subtitle">
              橋本病の方は特に注意が必要とされている成分です
            </p>
            <div className="ingredient-check-result__alerts-list">
              {alerts.hashimoto_high.map((alert, index) => (
                <AlertCard key={index} alert={alert} category="hashimoto_high" index={index} />
              ))}
            </div>
          </div>
        )}

        {/* 一般的に注意が必要な成分 */}
        {alerts.general_caution.length > 0 && (
          <div className="ingredient-check-result__section mb-4">
            <h3 className="ingredient-check-result__section-title ingredient-check-result__section-title--general">
              💡 一般的に注意が必要な成分
            </h3>
            <p className="ingredient-check-result__section-subtitle">
              橋本病に関わらず、一般的に過剰摂取を避けた方が良いとされている成分です
            </p>
            <div className="ingredient-check-result__alerts-list">
              {alerts.general_caution.map((alert, index) => (
                <AlertCard key={index} alert={alert} category="general_caution" index={index} />
              ))}
            </div>
          </div>
        )}

        {/* 適量推奨成分 */}
        {alerts.moderation.length > 0 && (
          <div className="ingredient-check-result__section mb-4">
            <h3 className="ingredient-check-result__section-title ingredient-check-result__section-title--moderation">
              ℹ️ 適量であれば問題ない成分
            </h3>
            <p className="ingredient-check-result__section-subtitle">
              適量であれば問題ないとされていますが、過剰摂取には注意してください
            </p>
            <div className="ingredient-check-result__alerts-list">
              {alerts.moderation.map((alert, index) => (
                <AlertCard key={index} alert={alert} category="moderation" index={index} />
              ))}
            </div>
          </div>
        )}

        {/* 問題のない成分 */}
        {!hasAlerts && (
          <div className="tsumugi-card tsumugi-card--mint ingredient-check-result__no-alerts mb-4">
            <div className="ingredient-check-result__no-alerts-icon">✅</div>
            <h3 className="ingredient-check-result__no-alerts-title">問題のある成分は検出されませんでした</h3>
            <p className="ingredient-check-result__no-alerts-text">
              この商品の成分表からは、橋本病の方が特に注意すべき成分や、一般的に避けた方が良い添加物は検出されませんでした。
            </p>
          </div>
        )}

        {/* アクションボタン */}
        <div className="flex gap-2 mb-3">
          <button
            className="tsumugi-btn tsumugi-btn-primary flex-1"
            onClick={handleCheckAgain}
          >
            🔍 別の商品をチェック
          </button>
          <button
            className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray flex-1"
            onClick={handleBackToRecipes}
          >
            レシピ一覧に戻る
          </button>
        </div>

        {/* 免責事項 */}
        <div className="ingredient-check-result__disclaimer">
          <div className="ingredient-check-result__disclaimer-content">
            <span className="ingredient-check-result__disclaimer-icon">ℹ️</span>
            <div>
              <p className="ingredient-check-result__disclaimer-title">この情報について</p>
              <p className="ingredient-check-result__disclaimer-text">
                この機能は一般的な栄養の知識に基づく情報提供です。医療行為ではありません。
                最終判断はご自身で行ってください。気になる場合は専門家にご相談いただけます。
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default IngredientCheckResultPage;
